import { useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { createTemplateApi } from "../../../../api/chat.api";
import {
  Box,
  Typography,
  Button,
  Paper,
  IconButton,
  TextField,
  Select,
  MenuItem,
  FormControl,
  Dialog,
  DialogContent,
  Menu,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  ArrowBack,
  DescriptionOutlined,
  EmojiEmotions,
  FormatBold,
  FormatItalic,
  StrikethroughS,
  Code,
  Add,
  Close,
  Info,
  Image as ImageIcon,
  VideoLibrary as VideoIcon,
  InsertDriveFile as DocumentIcon,
  CloudUpload,
} from "@mui/icons-material";
import IOSMobileFrame from "../../Templates/IOSMobileFrame";
import WhatsAppPreview from "../../Templates/WhatsAppPreview";

export default function CreateTemplateView() {
  const theme = useTheme();
  const primaryColor = theme.palette.primary.main;
  const primaryDark = theme.palette.primary.dark;
  const navigate = useNavigate();

  // Form state
  const [templateName, setTemplateName] = useState("");
  const [language, setLanguage] = useState("English");
  const [mediaSample, setMediaSample] = useState("None");
  const [uploadedMedia, setUploadedMedia] = useState(null);
  const fileInputRef = useRef(null);
  const [headerText, setHeaderText] = useState("");
  const [bodyText, setBodyText] = useState("");
  const [footerText, setFooterText] = useState("");
  const [buttons, setButtons] = useState([]);
  const [buttonMenuAnchor, setButtonMenuAnchor] = useState(null);
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const bodyTextRef = useRef(null);
  const headerTextRef = useRef(null);

  // Quick Reply buttons
  const [quickReplyButtons, setQuickReplyButtons] = useState([]);

  // Get next variable number from existing text
  const getNextVarNum = (text) => {
    const matches = text.match(/\{\{(\d+)\}\}/g) || [];
    if (matches.length === 0) return 1;
    const nums = matches.map((m) => parseInt(m.replace(/[{}]/g, ""), 10));
    return Math.max(...nums) + 1;
  };

  // ── Text formatting ──────────────────────────────────────────────
  const formatText = (prefix, suffix) => {
    const input = bodyTextRef.current?.querySelector("textarea");
    if (!input) return;
    const start = input.selectionStart;
    const end = input.selectionEnd;
    const selected = bodyText.substring(start, end);

    if (selected) {
      setBodyText(
        bodyText.substring(0, start) + prefix + selected + suffix + bodyText.substring(end),
      );
    } else {
      setBodyText(
        bodyText.substring(0, start) + prefix + suffix + bodyText.substring(end),
      );
      setTimeout(() => {
        input.focus();
        input.setSelectionRange(start + prefix.length, start + prefix.length);
      }, 0);
    }
  };

  const handleBold = () => formatText("*", "*");
  const handleItalic = () => formatText("_", "_");
  const handleStrikethrough = () => formatText("~", "~");
  const handleCode = () => formatText("```", "```");

  const handleAddBodyVariable = () => {
    const input = bodyTextRef.current?.querySelector("textarea");
    if (!input) return;
    const start = input.selectionStart;
    const variable = `{{${getNextVarNum(bodyText)}}}`;
    setBodyText(bodyText.substring(0, start) + variable + bodyText.substring(start));
    setTimeout(() => {
      input.focus();
      input.setSelectionRange(start + variable.length, start + variable.length);
    }, 0);
  };

  const handleAddHeaderVariable = () => {
    const next = getNextVarNum(headerText);
    setHeaderText((prev) => prev + `{{${next}}}`);
  };

  // ── Quick Reply management ───────────────────────────────────────
  const handleAddQuickReply = () => {
    if (quickReplyButtons.length < 3) {
      setQuickReplyButtons([...quickReplyButtons, { type: "custom", text: "" }]);
    }
  };

  const handleUpdateQuickReply = (index, field, value) => {
    if (field === "text" && value.length > 25) return;
    const updated = [...quickReplyButtons];
    updated[index] = { ...updated[index], [field]: value };
    setQuickReplyButtons(updated);
  };

  const handleRemoveQuickReply = (index) => {
    setQuickReplyButtons(quickReplyButtons.filter((_, i) => i !== index));
  };

  // ── CTA button management ────────────────────────────────────────
  const handleAddButton = () => {
    if (buttons.length < 2) {
      setButtons([...buttons, { type: "visit_website", text: "", url: "" }]);
    }
  };

  const handleUpdateButton = (index, field, value) => {
    if (field === "text" && value.length > 25) return;
    const updated = [...buttons];
    updated[index] = { ...updated[index], [field]: value };
    setButtons(updated);
  };

  const handleRemoveButton = (index) => {
    setButtons(buttons.filter((_, i) => i !== index));
  };

  const getButtonTypeLabel = (type) => {
    switch (type) {
      case "call_whatsapp": return "Call on WhatsApp";
      case "visit_website": return "Visit website";
      case "copy_code": return "Copy offer code";
      default: return type;
    }
  };

  // ── Media upload ─────────────────────────────────────────────────
  const getAcceptedFileTypes = () => {
    switch (mediaSample) {
      case "Image": return "image/jpeg,image/png,image/webp";
      case "Video": return "video/mp4,video/3gpp";
      case "Document": return "application/pdf";
      default: return "*/*";
    }
  };

  const getMaxFileSize = () => {
    switch (mediaSample) {
      case "Image": return 5 * 1024 * 1024;
      case "Video": return 16 * 1024 * 1024;
      case "Document": return 100 * 1024 * 1024;
      default: return 5 * 1024 * 1024;
    }
  };

  const handleFileUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (file.size > getMaxFileSize()) {
      alert(`File size exceeds ${getMaxFileSize() / (1024 * 1024)}MB limit`);
      return;
    }
    setUploadedMedia({
      name: file.name,
      url: URL.createObjectURL(file),
      type: mediaSample,
      file,
    });
    event.target.value = "";
  };

  const handleRemoveMedia = () => {
    if (uploadedMedia?.url) URL.revokeObjectURL(uploadedMedia.url);
    setUploadedMedia(null);
  };

  const handleMediaTypeChange = (newType) => {
    if (uploadedMedia) handleRemoveMedia();
    setMediaSample(newType);
  };

  const getMediaIcon = () => {
    switch (mediaSample) {
      case "Image": return <ImageIcon sx={{ fontSize: 20, color: "#f59e0b" }} />;
      case "Video": return <VideoIcon sx={{ fontSize: 20, color: "#3b82f6" }} />;
      case "Document": return <DocumentIcon sx={{ fontSize: 20, color: "#ef4444" }} />;
      default: return <ImageIcon sx={{ fontSize: 20, color: "#f59e0b" }} />;
    }
  };

  const getMediaIconBgColor = () => {
    switch (mediaSample) {
      case "Image": return "#fef3c7";
      case "Video": return "#dbeafe";
      case "Document": return "#fee2e2";
      default: return "#fef3c7";
    }
  };

  // ── Submit ───────────────────────────────────────────────────────
  const handleSubmitConfirm = useCallback(async () => {
    setShowSubmitModal(false);
    setSubmitting(true);
    try {
      let headerFormat = "NONE";
      if (headerText) headerFormat = "TEXT";
      else if (uploadedMedia) headerFormat = uploadedMedia.type.toUpperCase();

      await createTemplateApi({
        name: templateName,
        category: "MARKETING",
        language,
        headerText: headerText || "",
        headerFormat,
        bodyText,
        footerText: footerText || "",
        buttons,
        quickReplyButtons,
      });
      navigate("/chat/templates");
    } catch (err) {
      console.error("Template creation failed:", err);
    } finally {
      setSubmitting(false);
    }
  }, [templateName, language, headerText, uploadedMedia, bodyText, footerText, buttons, quickReplyButtons, navigate]);

  // ── Build preview data ───────────────────────────────────────────
  const previewData = {
    header:
      uploadedMedia
        ? {
            media: {
              type: uploadedMedia.type === "Image" ? "IMAGE" : uploadedMedia.type === "Video" ? "VIDEO" : "DOCUMENT",
              url: uploadedMedia.url,
              name: uploadedMedia.name,
            },
          }
        : headerText
          ? { text: headerText }
          : {},
    body: bodyText || "Your message body here...",
    footer: footerText,
    buttons: [
      ...quickReplyButtons.map((qr) => ({
        type: "QUICK_REPLY",
        text: qr.text || "Quick Reply",
      })),
      ...buttons.map((btn) => ({
        type: btn.type === "call_whatsapp" ? "CALL" : btn.type === "visit_website" ? "URL" : "COPY",
        text: btn.text || getButtonTypeLabel(btn.type),
      })),
    ],
  };

  const canSubmit = templateName.trim() && bodyText.trim();

  // ── Render ───────────────────────────────────────────────────────
  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", bgcolor: "#f8fafc" }}>
      {/* ── Top Header ── */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 3,
          py: 1.5,
          bgcolor: "#fff",
          borderBottom: "1px solid #e2e8f0",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <IconButton
            onClick={() => navigate("/chat/templates")}
            sx={{ color: "#64748b", width: 32, height: 32, "&:hover": { bgcolor: "#f1f5f9", color: "#1e293b" } }}
          >
            <ArrowBack sx={{ fontSize: 18 }} />
          </IconButton>
          <Typography sx={{ fontSize: "0.875rem", color: "#64748b" }}>
            <Box
              component="span"
              sx={{ cursor: "pointer", "&:hover": { textDecoration: "underline" } }}
              onClick={() => navigate("/chat/inbox")}
            >
              Chat
            </Box>
            {" / "}
            <Box
              component="span"
              sx={{ cursor: "pointer", "&:hover": { textDecoration: "underline" } }}
              onClick={() => navigate("/chat/templates")}
            >
              Templates
            </Box>
            {" / "}
            <Box component="span" sx={{ color: primaryColor, fontWeight: 600 }}>
              {templateName || "New Template"}
            </Box>
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<DescriptionOutlined sx={{ fontSize: 16 }} />}
          onClick={() => navigate("/chat/templates")}
          sx={{
            textTransform: "none",
            color: primaryColor,
            fontWeight: 500,
            fontSize: "0.8125rem",
            px: 1,
            py: 0.75,
            borderRadius: "10px",
            border: `1.5px solid ${primaryColor}`,
            "&:hover": { bgcolor: alpha(primaryColor, 0.08), borderColor: primaryDark },
          }}
        >
          Manage Templates
        </Button>
      </Box>

      {/* ── Main Content ── */}
      <Box sx={{ flex: 1, display: "flex", overflow: "hidden" }}>
        {/* ── Left: Editor ── */}
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            p: 2,
            "&::-webkit-scrollbar": { width: "6px" },
            "&::-webkit-scrollbar-track": { background: "transparent" },
            "&::-webkit-scrollbar-thumb": { background: "#e2e8f0", borderRadius: "10px" },
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {/* Template header badge */}
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                borderRadius: "12px",
                border: "1px solid #e2e8f0",
                bgcolor: "#fff",
                display: "flex",
                alignItems: "center",
                gap: 2,
              }}
            >
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: "8px",
                  bgcolor: "#f3e8ff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <DescriptionOutlined sx={{ fontSize: 20, color: primaryColor }} />
              </Box>
              <Box>
                <Typography sx={{ fontWeight: 600, fontSize: "1rem", color: "#1e293b" }}>
                  {templateName || "your_template_name"} &bull; {language}
                </Typography>
                <Typography sx={{ fontSize: "0.8125rem", color: "#64748b" }}>
                  Marketing &bull; Default
                </Typography>
              </Box>
            </Paper>

            {/* ── Name & Language ── */}
            <Paper
              elevation={0}
              sx={{ p: 3, borderRadius: "12px", border: "1px solid #e2e8f0", bgcolor: "#fff" }}
            >
              <Typography sx={{ fontWeight: 600, fontSize: "1rem", color: "#1e293b", mb: 2 }}>
                Template name and language
              </Typography>
              <Box sx={{ display: "flex", gap: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    sx={{ fontSize: "0.75rem", fontWeight: 500, color: "#64748b", textTransform: "uppercase", mb: 0.75 }}
                  >
                    Name your template
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    value={templateName}
                    onChange={(e) => {
                      const val = e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "_");
                      if (val.length <= 512) setTemplateName(val);
                    }}
                    placeholder="enter_template_name"
                    helperText="Only lowercase letters, numbers & underscores"
                    InputProps={{
                      endAdornment: (
                        <Typography sx={{ fontSize: "0.75rem", color: "#94a3b8" }}>
                          {templateName.length}/512
                        </Typography>
                      ),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "8px",
                        bgcolor: "#f8fafc",
                        "& fieldset": { borderColor: "#e2e8f0" },
                        "&:hover fieldset": { borderColor: "#cbd5e1" },
                        "&.Mui-focused fieldset": { borderColor: primaryColor },
                      },
                    }}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    sx={{ fontSize: "0.75rem", fontWeight: 500, color: "#64748b", textTransform: "uppercase", mb: 0.75 }}
                  >
                    Select language
                  </Typography>
                  <FormControl fullWidth size="small">
                    <Select
                      value={language}
                      onChange={(e) => setLanguage(e.target.value)}
                      sx={{
                        borderRadius: "8px",
                        bgcolor: "#f8fafc",
                        "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e2e8f0" },
                        "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#cbd5e1" },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: primaryColor },
                      }}
                    >
                      <MenuItem value="English">English</MenuItem>
                      <MenuItem value="Hindi">Hindi</MenuItem>
                      <MenuItem value="Tamil">Tamil</MenuItem>
                      <MenuItem value="Spanish">Spanish</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              </Box>
            </Paper>

            {/* ── Content ── */}
            <Paper
              elevation={0}
              sx={{ p: 3, borderRadius: "12px", border: "1px solid #e2e8f0", bgcolor: "#fff" }}
            >
              <Typography sx={{ fontWeight: 600, fontSize: "1rem", color: "#1e293b", mb: 0.5 }}>
                Content
              </Typography>
              <Typography sx={{ fontSize: "0.8125rem", color: "#64748b", mb: 2.5 }}>
                Add a header, body and footer for your template. Meta will review the content before approval.
              </Typography>

              {/* Media sample select */}
              <Box sx={{ mb: 2.5 }}>
                <Typography
                  sx={{ fontSize: "0.75rem", fontWeight: 500, color: "#64748b", textTransform: "uppercase", mb: 0.75 }}
                >
                  Media sample <Box component="span" sx={{ color: "#94a3b8", fontWeight: 400, textTransform: "none" }}>&bull; Optional</Box>
                </Typography>
                <FormControl fullWidth size="small">
                  <Select
                    value={mediaSample}
                    onChange={(e) => handleMediaTypeChange(e.target.value)}
                    sx={{
                      borderRadius: "8px",
                      bgcolor: "#f8fafc",
                      "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e2e8f0" },
                    }}
                  >
                    <MenuItem value="None">None</MenuItem>
                    <MenuItem value="Image">Image</MenuItem>
                    <MenuItem value="Video">Video</MenuItem>
                    <MenuItem value="Document">Document</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              {/* Media upload */}
              {mediaSample !== "None" && (
                <>
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileUpload}
                    accept={getAcceptedFileTypes()}
                    style={{ display: "none" }}
                  />
                  {uploadedMedia ? (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        p: 1.5,
                        mb: 2.5,
                        borderRadius: "8px",
                        bgcolor: "#f8fafc",
                        border: "1px solid #e2e8f0",
                      }}
                    >
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: "8px",
                          bgcolor: getMediaIconBgColor(),
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          overflow: "hidden",
                        }}
                      >
                        {uploadedMedia.type === "Image" && uploadedMedia.url ? (
                          <img
                            src={uploadedMedia.url}
                            alt={uploadedMedia.name}
                            style={{ width: "100%", height: "100%", objectFit: "cover" }}
                          />
                        ) : (
                          getMediaIcon()
                        )}
                      </Box>
                      <Typography sx={{ flex: 1, fontSize: "0.875rem", color: "#1e293b" }}>
                        {uploadedMedia.name}
                      </Typography>
                      <IconButton size="small" onClick={handleRemoveMedia} sx={{ color: "#64748b" }}>
                        <Close sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Box>
                  ) : (
                    <Box
                      onClick={() => fileInputRef.current?.click()}
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 1,
                        p: 3,
                        mb: 2.5,
                        borderRadius: "8px",
                        bgcolor: "#f8fafc",
                        border: "2px dashed #e2e8f0",
                        cursor: "pointer",
                        transition: "all 0.2s",
                        "&:hover": { borderColor: primaryColor, bgcolor: "#faf5ff" },
                      }}
                    >
                      <Box
                        sx={{
                          width: 48,
                          height: 48,
                          borderRadius: "50%",
                          bgcolor: "#ede9fe",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <CloudUpload sx={{ fontSize: 24, color: primaryColor }} />
                      </Box>
                      <Typography sx={{ fontSize: "0.875rem", fontWeight: 500, color: "#1e293b" }}>
                        Click to upload {mediaSample.toLowerCase()}
                      </Typography>
                      <Typography sx={{ fontSize: "0.75rem", color: "#64748b", textAlign: "center" }}>
                        {mediaSample === "Image" && "JPG, PNG or WEBP (max. 5MB)"}
                        {mediaSample === "Video" && "MP4 or 3GPP (max. 16MB)"}
                        {mediaSample === "Document" && "PDF (max. 100MB)"}
                      </Typography>
                    </Box>
                  )}
                </>
              )}

              {/* Header */}
              <Box sx={{ mb: 2.5 }}>
                <Typography
                  sx={{ fontSize: "0.75rem", fontWeight: 500, color: "#64748b", textTransform: "uppercase", mb: 0.75 }}
                >
                  Header <Box component="span" sx={{ color: "#94a3b8", fontWeight: 400, textTransform: "none" }}>&bull; Optional</Box>
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  inputRef={headerTextRef}
                  value={headerText}
                  onChange={(e) => {
                    if (e.target.value.length <= 60) setHeaderText(e.target.value);
                  }}
                  placeholder="Add a short header line"
                  InputProps={{
                    endAdornment: (
                      <Typography sx={{ fontSize: "0.75rem", color: "#94a3b8", whiteSpace: "nowrap" }}>
                        {headerText.length}/60
                      </Typography>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px",
                      bgcolor: "#f8fafc",
                      "& fieldset": { borderColor: "#e2e8f0" },
                    },
                  }}
                />
                <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 0.75 }}>
                  <Typography
                    component="span"
                    onClick={handleAddHeaderVariable}
                    sx={{
                      fontSize: "0.8125rem",
                      color: "#1e293b",
                      fontWeight: 500,
                      cursor: "pointer",
                      "&:hover": { color: primaryColor },
                    }}
                  >
                    + Add variable
                  </Typography>
                </Box>
              </Box>

              {/* Body */}
              <Box sx={{ mb: 2.5 }}>
                <Typography
                  sx={{ fontSize: "0.75rem", fontWeight: 500, color: "#64748b", textTransform: "uppercase", mb: 0.75 }}
                >
                  Body
                </Typography>
                <Box ref={bodyTextRef}>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    value={bodyText}
                    onChange={(e) => {
                      if (e.target.value.length <= 1024) setBodyText(e.target.value);
                    }}
                    placeholder="Enter message body"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "8px",
                        bgcolor: "#f8fafc",
                        "& fieldset": { borderColor: "#e2e8f0" },
                      },
                    }}
                  />
                </Box>
                {/* Formatting toolbar */}
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: 1 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <IconButton size="small" sx={{ color: "#64748b" }} title="Emoji">
                      <EmojiEmotions sx={{ fontSize: 18 }} />
                    </IconButton>
                    <IconButton size="small" sx={{ color: "#64748b" }} onClick={handleBold} title="Bold (*text*)">
                      <FormatBold sx={{ fontSize: 18 }} />
                    </IconButton>
                    <IconButton size="small" sx={{ color: "#64748b" }} onClick={handleItalic} title="Italic (_text_)">
                      <FormatItalic sx={{ fontSize: 18 }} />
                    </IconButton>
                    <IconButton size="small" sx={{ color: "#64748b" }} onClick={handleStrikethrough} title="Strikethrough (~text~)">
                      <StrikethroughS sx={{ fontSize: 18 }} />
                    </IconButton>
                    <IconButton size="small" sx={{ color: "#64748b" }} onClick={handleCode} title="Code (```text```)">
                      <Code sx={{ fontSize: 18 }} />
                    </IconButton>
                    <Box sx={{ borderLeft: "1px solid #e2e8f0", height: 20, mx: 0.5 }} />
                    <Box
                      component="span"
                      onClick={handleAddBodyVariable}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        fontSize: "0.8125rem",
                        color: "#1e293b",
                        fontWeight: 500,
                        cursor: "pointer",
                        "&:hover": { color: primaryColor },
                      }}
                    >
                      + Add variable
                      <Info sx={{ fontSize: 14, color: "#94a3b8" }} />
                    </Box>
                  </Box>
                  <Typography sx={{ fontSize: "0.75rem", color: "#94a3b8" }}>
                    {bodyText.length} / 1024
                  </Typography>
                </Box>
              </Box>

              {/* Footer */}
              <Box>
                <Typography
                  sx={{ fontSize: "0.75rem", fontWeight: 500, color: "#64748b", textTransform: "uppercase", mb: 0.75 }}
                >
                  Footer <Box component="span" sx={{ color: "#94a3b8", fontWeight: 400, textTransform: "none" }}>&bull; Optional</Box>
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  value={footerText}
                  onChange={(e) => {
                    if (e.target.value.length <= 60) setFooterText(e.target.value);
                  }}
                  placeholder="Add a short footer line"
                  InputProps={{
                    endAdornment: (
                      <Typography sx={{ fontSize: "0.75rem", color: "#94a3b8", whiteSpace: "nowrap" }}>
                        {footerText.length}/60
                      </Typography>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px",
                      bgcolor: "#f8fafc",
                      "& fieldset": { borderColor: "#e2e8f0" },
                    },
                  }}
                />
              </Box>
            </Paper>

            {/* ── Buttons Section ── */}
            <Paper
              elevation={0}
              sx={{ p: 3, borderRadius: "12px", border: "1px solid #e2e8f0", bgcolor: "#fff" }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
                <Typography sx={{ fontWeight: 600, fontSize: "1rem", color: "#1e293b" }}>
                  Buttons
                </Typography>
                <Typography sx={{ fontSize: "0.875rem", color: "#94a3b8" }}>&bull; Optional</Typography>
              </Box>
              <Typography sx={{ fontSize: "0.8125rem", color: "#64748b", mb: 2 }}>
                Create buttons that let customers respond to your message or take action.
              </Typography>

              {/* Add button dropdown */}
              <Button
                variant="outlined"
                size="small"
                startIcon={<Add sx={{ fontSize: 16 }} />}
                endIcon={
                  <Box
                    component="span"
                    sx={{
                      width: 0,
                      height: 0,
                      borderLeft: "4px solid transparent",
                      borderRight: "4px solid transparent",
                      borderTop: "4px solid #64748b",
                      ml: 0.5,
                    }}
                  />
                }
                onClick={(e) => setButtonMenuAnchor(e.currentTarget)}
                disabled={quickReplyButtons.length >= 3 && buttons.length >= 2}
                sx={{
                  textTransform: "none",
                  color: "#1e293b",
                  borderColor: "#e2e8f0",
                  borderRadius: "8px",
                  fontWeight: 500,
                  fontSize: "0.875rem",
                  mb: 2,
                  "&:hover": { bgcolor: "#f8fafc", borderColor: "#cbd5e1" },
                }}
              >
                Add button
              </Button>

              <Menu
                anchorEl={buttonMenuAnchor}
                open={Boolean(buttonMenuAnchor)}
                onClose={() => setButtonMenuAnchor(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
              >
                <MenuItem
                  onClick={() => { handleAddQuickReply(); setButtonMenuAnchor(null); }}
                  disabled={quickReplyButtons.length >= 3}
                >
                  Quick Reply
                </MenuItem>
                <MenuItem
                  onClick={() => { handleAddButton(); setButtonMenuAnchor(null); }}
                  disabled={buttons.length >= 2}
                >
                  Call to Action
                </MenuItem>
              </Menu>

              {/* Quick Reply buttons */}
              {quickReplyButtons.map((qr, index) => (
                <Box
                  key={`qr-${index}`}
                  sx={{ p: 2, borderRadius: "8px", border: "1px solid #e2e8f0", bgcolor: "#f8fafc", mb: 2 }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
                    <Typography
                      sx={{ fontSize: "0.6875rem", fontWeight: 600, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em" }}
                    >
                      Quick Reply
                    </Typography>
                    <IconButton size="small" onClick={() => handleRemoveQuickReply(index)} sx={{ color: "#94a3b8" }}>
                      <Close sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Box>
                  <Box sx={{ display: "flex", gap: 2 }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{ fontSize: "0.75rem", fontWeight: 500, color: "#64748b", mb: 0.75 }}>
                        Type
                      </Typography>
                      <FormControl fullWidth size="small">
                        <Select
                          value={qr.type}
                          onChange={(e) => handleUpdateQuickReply(index, "type", e.target.value)}
                          sx={{
                            borderRadius: "8px",
                            bgcolor: "#fff",
                            "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e2e8f0" },
                          }}
                        >
                          <MenuItem value="custom">Custom</MenuItem>
                          <MenuItem value="marketing_opt_out">Marketing opt-out</MenuItem>
                        </Select>
                      </FormControl>
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{ fontSize: "0.75rem", fontWeight: 500, color: "#64748b", mb: 0.75 }}>
                        Button text
                      </Typography>
                      <TextField
                        fullWidth
                        size="small"
                        value={qr.text}
                        onChange={(e) => handleUpdateQuickReply(index, "text", e.target.value)}
                        placeholder="Quick Reply"
                        InputProps={{
                          endAdornment: (
                            <Typography
                              sx={{ fontSize: "0.75rem", color: qr.text.length === 25 ? "#ef4444" : "#94a3b8", whiteSpace: "nowrap" }}
                            >
                              {qr.text.length}/25
                            </Typography>
                          ),
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "8px",
                            bgcolor: "#fff",
                            "& fieldset": { borderColor: "#e2e8f0" },
                          },
                        }}
                      />
                    </Box>
                  </Box>
                </Box>
              ))}

              {/* CTA buttons */}
              {buttons.map((button, index) => (
                <Box
                  key={`cta-${index}`}
                  sx={{ p: 2, borderRadius: "8px", border: "1px solid #e2e8f0", bgcolor: "#f8fafc", mb: 2 }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
                    <Typography
                      sx={{ fontSize: "0.6875rem", fontWeight: 600, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em" }}
                    >
                      Call to Action
                    </Typography>
                    <IconButton size="small" onClick={() => handleRemoveButton(index)} sx={{ color: "#94a3b8" }}>
                      <Close sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Box>
                  <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{ fontSize: "0.75rem", fontWeight: 500, color: "#64748b", mb: 0.75 }}>
                        Type of action
                      </Typography>
                      <FormControl fullWidth size="small">
                        <Select
                          value={button.type}
                          onChange={(e) => handleUpdateButton(index, "type", e.target.value)}
                          sx={{
                            borderRadius: "8px",
                            bgcolor: "#fff",
                            "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e2e8f0" },
                          }}
                        >
                          <MenuItem value="visit_website">Visit website</MenuItem>
                          <MenuItem value="call_whatsapp">Call on WhatsApp</MenuItem>
                          <MenuItem value="copy_code">Copy offer code</MenuItem>
                        </Select>
                      </FormControl>
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{ fontSize: "0.75rem", fontWeight: 500, color: "#64748b", mb: 0.75 }}>
                        Button text
                      </Typography>
                      <TextField
                        fullWidth
                        size="small"
                        value={button.text}
                        onChange={(e) => handleUpdateButton(index, "text", e.target.value)}
                        placeholder={getButtonTypeLabel(button.type)}
                        InputProps={{
                          endAdornment: (
                            <Typography
                              sx={{ fontSize: "0.75rem", color: button.text.length === 25 ? "#ef4444" : "#94a3b8", whiteSpace: "nowrap" }}
                            >
                              {button.text.length}/25
                            </Typography>
                          ),
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "8px",
                            bgcolor: "#fff",
                            "& fieldset": { borderColor: "#e2e8f0" },
                          },
                        }}
                      />
                    </Box>
                  </Box>

                  {/* URL field for visit_website */}
                  {button.type === "visit_website" && (
                    <Box>
                      <Typography sx={{ fontSize: "0.75rem", fontWeight: 500, color: "#64748b", mb: 0.75 }}>
                        Website URL
                      </Typography>
                      <TextField
                        fullWidth
                        size="small"
                        value={button.url || ""}
                        onChange={(e) => handleUpdateButton(index, "url", e.target.value)}
                        placeholder="https://example.com"
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "8px",
                            bgcolor: "#fff",
                            "& fieldset": { borderColor: "#e2e8f0" },
                          },
                        }}
                      />
                    </Box>
                  )}

                  {/* Phone field for call_whatsapp */}
                  {button.type === "call_whatsapp" && (
                    <Box>
                      <Typography sx={{ fontSize: "0.75rem", fontWeight: 500, color: "#64748b", mb: 0.75 }}>
                        Phone number
                      </Typography>
                      <TextField
                        fullWidth
                        size="small"
                        value={button.phoneNumber || ""}
                        onChange={(e) => handleUpdateButton(index, "phoneNumber", e.target.value)}
                        placeholder="+91XXXXXXXXXX"
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "8px",
                            bgcolor: "#fff",
                            "& fieldset": { borderColor: "#e2e8f0" },
                          },
                        }}
                      />
                    </Box>
                  )}

                  {/* Code field for copy_code */}
                  {button.type === "copy_code" && (
                    <Box>
                      <Typography sx={{ fontSize: "0.75rem", fontWeight: 500, color: "#64748b", mb: 0.75 }}>
                        Offer code
                      </Typography>
                      <TextField
                        fullWidth
                        size="small"
                        value={button.code || ""}
                        onChange={(e) => handleUpdateButton(index, "code", e.target.value)}
                        placeholder="SAVE20"
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "8px",
                            bgcolor: "#fff",
                            "& fieldset": { borderColor: "#e2e8f0" },
                          },
                        }}
                      />
                    </Box>
                  )}
                </Box>
              ))}
            </Paper>
          </Box>
        </Box>

        {/* ── Right: Preview ── */}
        <Box
          sx={{
            width: 380,
            display: "flex",
            flexDirection: "column",
            p: 2,
            overflowY: "auto",
            "&::-webkit-scrollbar": { width: "6px" },
            "&::-webkit-scrollbar-track": { background: "transparent" },
            "&::-webkit-scrollbar-thumb": { background: "#e2e8f0", borderRadius: "10px" },
          }}
        >
          <Typography sx={{ fontSize: "1rem", fontWeight: 700, color: "#1e293b", mb: 2 }}>
            Template Preview
          </Typography>
          <Paper
            elevation={0}
            sx={{ p: 2, borderRadius: "12px", border: "1px solid #e2e8f0", bgcolor: "#fff" }}
          >
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "flex-start" }}>
              <Box sx={{ width: "100%", maxWidth: 280 }}>
                <IOSMobileFrame>
                  <WhatsAppPreview data={previewData} />
                </IOSMobileFrame>
              </Box>
            </Box>
          </Paper>
        </Box>
      </Box>

      {/* ── Footer Actions ── */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          gap: 2,
          px: 3,
          py: 2,
          bgcolor: "#fff",
          borderTop: "1px solid #e2e8f0",
        }}
      >
        <Button
          variant="outlined"
          onClick={() => navigate("/chat/templates")}
          sx={{
            textTransform: "none",
            color: "#64748b",
            borderColor: "#e2e8f0",
            fontWeight: 500,
            fontSize: "0.875rem",
            px: 3,
            py: 1,
            borderRadius: "8px",
            "&:hover": { bgcolor: "#f8fafc", borderColor: "#cbd5e1" },
          }}
        >
          Discard
        </Button>
        <Button
          variant="contained"
          onClick={() => setShowSubmitModal(true)}
          disabled={!canSubmit || submitting}
          disableElevation
          sx={{
            textTransform: "none",
            bgcolor: primaryColor,
            color: "#fff",
            fontWeight: 600,
            fontSize: "0.875rem",
            px: 3,
            py: 1,
            borderRadius: "8px",
            "&:hover": { bgcolor: primaryDark },
          }}
        >
          Submit for Review
        </Button>
      </Box>

      {/* ── Submit Confirmation Modal ── */}
      <Dialog
        open={showSubmitModal}
        onClose={() => !submitting && setShowSubmitModal(false)}
        PaperProps={{ sx: { borderRadius: "12px", maxWidth: 480, width: "100%", p: 0 } }}
      >
        <DialogContent sx={{ p: 3 }}>
          <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
            <IconButton
              size="small"
              onClick={() => setShowSubmitModal(false)}
              disabled={submitting}
              sx={{ color: "#64748b" }}
            >
              <Close sx={{ fontSize: 20 }} />
            </IconButton>
          </Box>

          <Typography sx={{ fontSize: "1.25rem", fontWeight: 700, color: "#1e293b", mb: 2, pr: 4 }}>
            Submit template for review?
          </Typography>
          <Typography sx={{ fontSize: "0.875rem", color: "#64748b", lineHeight: 1.6, mb: 3 }}>
            Your template <strong>{templateName}</strong> will be submitted to Meta for review. This
            usually takes a few minutes but can take up to 24 hours. You&apos;ll be able to use the
            template once it&apos;s approved.
          </Typography>

          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5 }}>
            <Button
              onClick={() => setShowSubmitModal(false)}
              disabled={submitting}
              sx={{
                textTransform: "none",
                color: "#64748b",
                fontWeight: 500,
                fontSize: "0.875rem",
                px: 3,
                py: 1,
                borderRadius: "8px",
                "&:hover": { bgcolor: "#f8fafc" },
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmitConfirm}
              disabled={submitting}
              disableElevation
              sx={{
                textTransform: "none",
                bgcolor: primaryColor,
                color: "#fff",
                fontWeight: 600,
                fontSize: "0.875rem",
                px: 3,
                py: 1,
                borderRadius: "8px",
                "&:hover": { bgcolor: primaryDark },
              }}
            >
              {submitting ? "Submitting..." : "Submit"}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
}
